import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, TrendingDown, History, Pencil, Ban } from "lucide-react";
import { ReceiptForm, ReceiptDetail, VoidForm, inr } from "@/components/FundReceiptParts";

export default function FundReceipts() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [receipts, setReceipts] = useState([]);
  const [open, setOpen] = useState(false);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [dlg, setDlg] = useState(null);
  const canManage = [user?.role, ...(user?.roles || [])].some(r => ["Admin", "Accounts"].includes(r));

  const load = async () => {
    const q = selectedProject && selectedProject !== "__ALL__" ? `?project_id=${selectedProject}` : "";
    setReceipts((await api.get(`/fund-receipts${q}`)).data);
  };
  useEffect(() => { api.get("/projects").then(r => setProjects(r.data)); }, []);
  useEffect(() => { load(); }, [selectedProject]); // eslint-disable-line react-hooks/exhaustive-deps

  const active = receipts.filter(r => !r.voided);
  const totalReceived = active.filter(r => !r.is_recovery).reduce((s, r) => s + (r.amount || 0), 0);
  const totalRecovery = active.filter(r => r.is_recovery).reduce((s, r) => s + (r.amount || 0), 0);
  const done = () => { setDlg(null); load(); };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Grants & Receipts</div>
          <h1 className="font-display text-3xl font-extrabold mt-1">Fund Receipts</h1>
          <p className="text-sm text-slate-500 mt-1">Grants received from donors, tranches, and recoveries (which net against expenditure).</p>
        </div>
        <div className="flex gap-2 items-end flex-wrap">
          <div className="min-w-[200px]">
            <Label>Project (or all)</Label>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger data-testid="fr-project-select"><SelectValue placeholder="All projects" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__ALL__">All projects</SelectItem>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {canManage && (
            <div className="flex gap-2">
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild><Button className="bg-[#0F172A]" data-testid="new-receipt-btn"><Plus className="w-4 h-4 mr-1" /> Receipt</Button></DialogTrigger>
                <DialogContent>{open && <ReceiptForm projects={projects} onSaved={() => { setOpen(false); load(); }} isRecovery={false} />}</DialogContent>
              </Dialog>
              <Dialog open={recoveryOpen} onOpenChange={setRecoveryOpen}>
                <DialogTrigger asChild><Button variant="outline" data-testid="new-recovery-btn"><TrendingDown className="w-4 h-4 mr-1" /> Recovery</Button></DialogTrigger>
                <DialogContent>{recoveryOpen && <ReceiptForm projects={projects} onSaved={() => { setRecoveryOpen(false); load(); }} isRecovery={true} />}</DialogContent>
              </Dialog>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card><CardContent className="p-5">
          <div className="text-xs uppercase text-slate-500">Total Received</div>
          <div className="font-display font-bold text-2xl mt-1 text-emerald-700" data-testid="fr-total-received">{inr(totalReceived)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="text-xs uppercase text-slate-500">Total Recoveries</div>
          <div className="font-display font-bold text-2xl mt-1 text-amber-700" data-testid="fr-total-recovery">− {inr(totalRecovery)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="text-xs uppercase text-slate-500">Net Inflow</div>
          <div className="font-display font-bold text-2xl mt-1" data-testid="fr-total-net">{inr(totalReceived + totalRecovery)}</div>
          <div className="text-xs text-slate-500 mt-1">Recoveries reduce expenditure, not income · voided receipts excluded</div>
        </CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 bg-slate-50">
              <tr className="border-b">
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Payer</th>
                <th className="text-left px-3 py-2">Project</th>
                <th className="text-left px-3 py-2">Tranche</th>
                <th className="text-left px-3 py-2">Bank ref / UTR</th>
                <th className="text-right px-3 py-2">Amount</th>
                <th className="text-center px-3 py-2" data-testid="fr-header-type">Type</th>
                <th className="text-center px-3 py-2">Flags</th>
                {canManage && <th className="text-right px-3 py-2">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {receipts.map(r => <ReceiptRow key={r.id} r={r} projects={projects} canManage={canManage} setDlg={setDlg} />)}
              {receipts.length === 0 && <tr><td colSpan={9} className="text-center px-3 py-10 text-slate-500">No receipts yet.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-xl">
          {dlg?.mode === "edit" && <ReceiptForm projects={projects} receipt={dlg.r} onSaved={done} />}
          {dlg?.mode === "void" && <VoidForm r={dlg.r} onDone={done} />}
          {dlg?.mode === "detail" && <ReceiptDetail r={dlg.r} projects={projects} onEdit={() => setDlg({ mode: "edit", r: dlg.r })} onVoid={() => setDlg({ mode: "void", r: dlg.r })} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReceiptRow({ r, projects, canManage, setDlg }) {
  const v = r.voided;
  return (
    <tr className={`border-b border-slate-100 ${v ? "bg-slate-50 text-slate-400" : ""}`} data-testid={`receipt-${r.id}`}>
      <td className="px-3 py-2 font-mono text-xs">{r.receipt_date}</td>
      <td className="px-3 py-2">
        <button type="button" className={`font-medium hover:underline text-left ${v ? "line-through" : ""}`} onClick={() => setDlg({ mode: "detail", r })} data-testid={`receipt-open-${r.id}`}>{r.payer_name}</button>
        {r.edited && <button type="button" className="ml-2 inline-flex items-center gap-0.5 text-[10px] text-blue-700 hover:underline" onClick={() => setDlg({ mode: "detail", r })} data-testid={`receipt-edited-${r.id}`}><History className="w-3 h-3" /> Edited</button>}
        {v && <div className="text-[11px] text-rose-600" data-testid={`receipt-voided-${r.id}`}>Voided – {r.void_reason}</div>}
      </td>
      <td className="px-3 py-2">{projects.find(p => p.id === r.project_id)?.name || "General"}</td>
      <td className="px-3 py-2 text-xs">{r.tranche_ref || "—"}</td>
      <td className="px-3 py-2 font-mono text-xs">{r.bank_reference || "—"}</td>
      <td className={`px-3 py-2 text-right font-mono ${v ? "line-through" : r.is_recovery ? "text-amber-700" : "text-emerald-700"}`} data-testid={`receipt-amount-${r.id}`}>{r.is_recovery ? "−" : ""} {inr(r.amount)}</td>
      <td className="px-3 py-2 text-center">{v ? <Badge variant="outline" className="text-[10px]">Voided</Badge> : r.is_recovery ? <Badge variant="destructive" className="text-[10px]">Recovery</Badge> : (r.is_interest_income ? <Badge className="text-[10px] bg-blue-600">Interest</Badge> : <Badge className="text-[10px] bg-emerald-600">Grant</Badge>)}</td>
      <td className="px-3 py-2 text-center text-xs">
        {r.is_80g && <Badge variant="outline" className="text-[10px] mr-1">80G</Badge>}
        {r.is_fcra && <Badge variant="outline" className="text-[10px]">FCRA</Badge>}
      </td>
      {canManage && (
        <td className="px-3 py-2 text-right whitespace-nowrap">
          {r.can_edit && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setDlg({ mode: "edit", r })} data-testid={`receipt-edit-${r.id}`}><Pencil className="w-3 h-3 mr-1" /> Edit</Button>}
          {!r.can_edit && !v && <span className="text-[10px] text-slate-400" title={r.edit_block_reason} data-testid={`receipt-locked-${r.id}`}>Locked</span>}
          {r.can_void && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-rose-700" onClick={() => setDlg({ mode: "void", r })} data-testid={`receipt-void-${r.id}`}><Ban className="w-3 h-3 mr-1" /> Void</Button>}
        </td>
      )}
    </tr>
  );
}
